import useVuelidate from '@vuelidate/core';
import { email, helpers, minLength, required, sameAs } from '@vuelidate/validators';
import type { ComputedRef, Ref } from 'vue';
import { computed, watch } from 'vue';
import { emailBusyRule } from '../../../shared/validation/rules/email-busy-rule';
import type {
  CaptchaData,
  CompanyProfileData,
  RegistrationCompanyErrors,
  RegistrationProfileData,
  RegistrationProfileErrors,
  RegistrationStage,
} from '~/pages/registration/components/registration-stages.vue';
import { emailRule } from '~/shared/validation/rules/email-rule';
import { isTrueRule } from '~/shared/validation/rules/is-true-rule';
import { matchTo } from '~/shared/validation/rules/match-to';
import { passwordRule } from '~/shared/validation/rules/password-rule';
import { phoneNumberRule } from '~/shared/validation/rules/phone-number-rule';

const fieldRequired = helpers
  .withMessage(({ $params }) => `${$params.label} is required`, required);

const profileRules = {
  firstName: {
    fieldRequired: helpers.withParams({ label: 'First Name' }, fieldRequired),
    minLength: helpers.withMessage('First Name must be at least 3 characters', minLength(3)),
  },
  lastName: {
    fieldRequired: helpers.withParams({ label: 'Last Name' }, fieldRequired),
    minLength: helpers.withMessage('Last Name must be at least 3 characters', minLength(3)),
  },
  email: {
    fieldRequired: helpers.withParams({ label: 'Email' }, fieldRequired),
    emailRule,
    emailBusyRule,
  },
  phone: {
    fieldRequired: helpers.withParams({ label: 'Phone' }, fieldRequired),
    phoneNumberRule,
  },
  password: {
    fieldRequired: helpers.withParams({ label: 'Password' }, fieldRequired),
    minLength: helpers.withMessage('Password must be at least 8 characters', minLength(8)),
    passwordRule,
  },
  passwordConfirmation: {
    fieldRequired: helpers.withParams({ label: 'Password Confirmation' }, fieldRequired),
    sameAs: helpers.withMessage(
      'Passwords do not match',
      matchTo<RegistrationProfileData>(siblings => siblings.password),
    ),
  },
  acceptTerms: {
    isTrueRule: helpers.withMessage('Checkbox is required', isTrueRule),
  },
};

const companyRules = {
  name: {
    required,
    minLength: minLength(3),
  },
  url: {},
  sellsPerMonth: {},
  shopUrls: {},
};

const captchaRules = {
  token: {
    required,
    minLength: minLength(3),
  },
};

export function useRegistrationValidation(
  stage: ComputedRef<RegistrationStage>,
  data: {
    profileData: Ref<RegistrationProfileData>
    companyData: Ref<CompanyProfileData>
    captchaData: Ref<CaptchaData>
  },
) {
  const profileVuelidate = useVuelidate(profileRules, data.profileData, {
    $lazy: true,
  });

  watch(() => profileVuelidate.value.email.$errors, () => {
    console.log('errors', profileVuelidate.value.email.$errors);
  });

  const profileErrors = computed<RegistrationProfileErrors>(() => ({
    firstName: profileVuelidate.value.firstName.$errors[0]?.$message as string || '',
    lastName: profileVuelidate.value.lastName.$errors[0]?.$message as string || '',
    email: profileVuelidate.value.email.$errors[0]?.$message as string || '',
    phone: profileVuelidate.value.phone.$errors[0]?.$message as string || '',
    password: profileVuelidate.value.password.$errors[0]?.$message as string || '',
    passwordConfirmation: profileVuelidate.value.passwordConfirmation.$errors[0]?.$message as string || '',
    acceptTerms: profileVuelidate.value.acceptTerms.$errors[0]?.$message as string || '',
  }));

  const profileHasErrors = computed(() => profileVuelidate.value.$invalid);

  const companyVuelidate = useVuelidate(companyRules, data.companyData, {
    $lazy: true,
  });

  const companyErrors = computed<RegistrationCompanyErrors>(() => ({
    name: companyVuelidate.value.name.$errors[0]?.$message as string || '',
    url: companyVuelidate.value.url.$errors[0]?.$message as string || '',
    sellsPerMonth: companyVuelidate.value.sellsPerMonth.$errors[0]?.$message as string || '',
    shopUrls: companyVuelidate.value.shopUrls.$errors[0]?.$message as string || '',
  }));

  const companyHasErrors = computed(() => companyVuelidate.value.$invalid);

  const captchaVuelidate = useVuelidate(captchaRules, data.captchaData, {
    $lazy: true,
  });

  const captchaHasErrors = computed(() => captchaVuelidate.value.$invalid);

  const reset = (stage: RegistrationStage) => {
    if (stage === 'profile') {
      profileVuelidate.value.$reset();
    }

    if (stage === 'company') {
      companyVuelidate.value.$reset();
    }
  };

  const trigger = async () => {
    if (stage.value === 'profile') {
      await profileVuelidate.value.$validate();

      return !profileVuelidate.value.$invalid;
    }

    if (stage.value === 'company') {
      await companyVuelidate.value.$validate();

      return !companyVuelidate.value.$invalid;
    }

    if (stage.value === 'captcha') {
      await captchaVuelidate.value.$validate();
    }

    return !companyVuelidate.value.$invalid && !profileVuelidate.value.$invalid && !captchaVuelidate.value.$invalid;
  };

  return {
    profileErrors,
    profileHasErrors,
    companyErrors,
    companyHasErrors,
    captchaHasErrors,
    trigger,
    reset,
  };
}
